import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import ChangeoverType from 'src/models/changeover-type';
import ScheduledChangeover from 'src/models/scheduled-changeover';
import { OperatorNote, TaskEventType } from 'src/models/task-event';
import Changeover from 'src/models/changeover';
import Task from 'src/models/task';
import ChangeoverReview from 'src/models/changeover-review';
import { catchError, delay, retryWhen, take } from 'rxjs/operators';
import { Observable, of, throwError } from 'rxjs';
import Line from 'src/models/line';
import { LineDiff } from 'src/models/line-diff';
import * as moment from 'moment';
import usage from 'src/assets/json/changeOverUsages.json';
import timebloks from 'src/assets/json/timeblocks.json';
import factories from 'src/assets/json/factories.json';
@Injectable({
  providedIn: 'root'
})
export class ApiService {
  constructor(private readonly http: HttpClient) { }

  registerForLiveUpdates(lineId: string, username: string, connectionId: string, previousConnectionId: string | null = null)
    : Promise<void> {
    const url = `${environment.apiUrl}/live/lines/${encodeURIComponent(lineId)}/users/${encodeURIComponent(username)}/connections/${encodeURIComponent(connectionId)}`;
    const request = this.http.put<void>(url,
      {},
      previousConnectionId
        ? {
          params: {
            previousConnectionId: previousConnectionId
          }
        }
        : undefined
    ).pipe(
      retryWhen(errors => errors.pipe(
        delay(1000),
        take(2)
      )));
    return request.toPromise();
  }

  getLines()
    : Promise<Line[]> {
    const url = `${environment.apiUrl}/lines`;
    const request = this.http.get<Line[]>(url);
    return request.toPromise();
  }

  getLine(lineId: string)
    : Promise<Line> {
    const url = `${environment.apiUrl}/lines/${encodeURIComponent(lineId)}`;
    const request = this.http.get<Line>(url);
    return request.toPromise();
  }

  previewLineSetupChanges(lineSetupWorkbook: File)
    : Promise<LineDiff> {
    const url = `${environment.apiUrl}/lines`;
    const request = this.http.post<LineDiff>(url,
      lineSetupWorkbook,
      {
        params: {
          preview: 'true',
          filename: lineSetupWorkbook.name,
          mimeType: lineSetupWorkbook.type
        }
      });
    return request.toPromise();
  }

  applyLineSetup(lineSetupWorkbook: File)
    : Promise<void> {
    const url = `${environment.apiUrl}/lines`;
    const request = this.http.post<void>(url,
      lineSetupWorkbook,
      {
        params: {
          filename: lineSetupWorkbook.name,
          mimeType: lineSetupWorkbook.type
        }
      });
    return request.toPromise();
  }

  getChangeoverTypes(lineId: string)
    : Promise<Array<ChangeoverType>> {
    const url = `${environment.apiUrl}/lines/${encodeURIComponent(lineId)}/changeover-types`;
    const request = this.http.get<Array<ChangeoverType>>(url);
    return request.toPromise();
  }

  getScheduledChangeovers(lineId: string)
    : Promise<Array<ScheduledChangeover>> {
    const url = `${environment.apiUrl}/lines/${encodeURIComponent(lineId)}/changeovers`;
    const request = this.http.get<Array<ScheduledChangeover>>(url);
    return request.toPromise();
  }

  scheduleChangeover(changeoverTypeId: string, fromProductId: string, toProductId: string, time: string)
    : Promise<string> {
    const url = `${environment.apiUrl}/changeovers`;
    const request = this.http.post<string>(url, {
      changeoverTypeId: changeoverTypeId,
      fromProductId: fromProductId,
      toProductId: toProductId,
      time: time,
    });
    return request.toPromise();
  }

  getChangeover(changeoverId: string)
    : Promise<Changeover> {
    const url = `${environment.apiUrl}/changeovers/${encodeURIComponent(changeoverId)}`;
    const request = this.http.get<Changeover>(url);
    return request.toPromise();
  }

  getChangeoverTask(changeoverId: string, taskId: string)
    : Promise<Task> {
    const url = `${environment.apiUrl}/changeovers/${encodeURIComponent(changeoverId)}/tasks/${taskId}`;
    const request = this.http.get<Task>(url);
    return request.toPromise();
  }

  deleteChangeover(changeoverId: string)
    : Promise<void> {
    const url = `${environment.apiUrl}/changeovers/${encodeURIComponent(changeoverId)}`;
    const request = this.http.delete<void>(url);
    return request.toPromise();
  }

  getNextChangeover(lineId: string)
    : Promise<Changeover | null> {
    const url = `${environment.apiUrl}/lines/${encodeURIComponent(lineId)}/changeovers/next`;
    const request = this.http.get<Changeover>(url);
    return request.toPromise();
  }

  getCurrentChangeover(lineId: string)
    : Promise<Changeover | null> {
    const url = `${environment.apiUrl}/lines/${encodeURIComponent(lineId)}/changeovers/current`;
    const request = this.http.get<Changeover>(url);
    return request.toPromise();
  }

  setCurrentChangeover(lineId: string, changeoverId: string)
    : Promise<void> {
    const url = `${environment.apiUrl}/lines/${encodeURIComponent(lineId)}/changeovers/current`;
    const request = this.http.put<void>(url, { changeoverId: changeoverId });
    return request.toPromise();
  }

  getCurrentChangeoverReview(lineId: string)
    : Promise<ChangeoverReview | null> {
    const url = `${environment.apiUrl}/lines/${encodeURIComponent(lineId)}/changeovers/current/review`;
    const request = this.http.get<ChangeoverReview>(url);
    return this.treat404AsNull(request).toPromise();
  }

  saveTaskEvent(lineId: string, changeoverId: string, taskId: string, eventType: TaskEventType)
    : Promise<string> {
    const url = `${environment.apiUrl}/lines/${encodeURIComponent(lineId)}/changeovers/${encodeURIComponent(changeoverId)}/tasks/${encodeURIComponent(taskId)}/events?type=${encodeURIComponent(eventType)}`;
    const request = this.http.post<string>(url, {});
    return request.toPromise();
  }

  saveOperatorNote(taskEventId: string, note: OperatorNote | null)
    : Promise<void> {
    const url = `${environment.apiUrl}/taskEvents/${encodeURIComponent(taskEventId)}/operator-note`;
    const request = this.http.put<void>(url, {
      reasonId: note?.operatorNoteReason,
      operatorNote: note?.operatorNote
    });
    return request.toPromise();
  }

  updateTimeBlock(startTaskEventId: string, startTime: moment.Moment, endTaskEventId?: string, endTime?: moment.Moment)
    : Promise<void> {
    const url = `${environment.apiUrl}/taskEvents/${encodeURIComponent(startTaskEventId)}/time-block`;
    const request = this.http.put<void>(url, {
      startTaskEventId: startTaskEventId,
      startTime: startTime.toISOString(),
      endTaskEventId: endTaskEventId,
      endTime: endTime?.toISOString()
    });
    return request.toPromise();
  }

  finaliseChangeover(lineId: string, changeoverId: string)
    : Promise<void> {
    const url = `${environment.apiUrl}/lines/${encodeURIComponent(lineId)}/changeovers/${encodeURIComponent(changeoverId)}/is-finalised`;
    const request = this.http.put<void>(url, true);
    return request.toPromise();
  }

  resetDB(): Promise<string> {
    const url = `${environment.apiUrl}/dbReset`;
    const request = this.http.put(url, {}, { responseType: 'text' });
    return request.toPromise();
  }

  private treat404AsNull<T>(source: Observable<T>): Observable<T | null> {
    return source.pipe(catchError((error: HttpErrorResponse) => error.status == 404 ? of(null) : throwError(error)));
  }
  getFactoryList():Observable<any> {
    const url = `${environment.apiUrl}/factories`;
    return this.http.get(url);
  }

  getChangeOverUsage(factoryId:string |null):Observable<any> {
    const url = `${environment.apiUrl}/ChangeOverUsages/${factoryId}`;
    return this.http.get(url);
  }
  getTimeBlocks(factoryId:string |null):Observable<any> {
    const url = `${environment.apiUrl}/timeblocks/${factoryId}`;
    return this.http.get(url);
  }
}
